import { type HTMLAttributes, isValidElement, type ReactNode } from 'react';

import { HintText } from '@/lib/base/forms/hint-text';
import type { InputBaseProps } from '@/lib/base/forms/input/input';
import { TextField } from '@/lib/base/forms/input/input';
import { Label } from '@/lib/base/forms/input/label';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { sizes } from '@/lib/base/forms/select/select-shared';
import { cx, sortCx } from '@/utils/cx.ts';

interface InputPrefixProps extends HTMLAttributes<HTMLDivElement> {
  /** The position of the prefix. */
  position?: 'leading' | 'trailing';
  /** The size of the prefix — the shared field-control scale, matching the
   * `InputGroup` it sits in. */
  size?: SelectSize;
  /** Indicates that the prefix is disabled. */
  isDisabled?: boolean;
}

export const InputPrefix = ({
  isDisabled,
  children,
  ...props
}: InputPrefixProps) => (
  <span
    {...props}
    className={cx(
      'flex text-md text-tertiary shadow-xs ring-1 ring-border-primary ring-inset',
      // Styles when the prefix is within an `InputGroup`
      'in-data-input-wrapper:in-data-leading:-mr-px in-data-input-wrapper:in-data-leading:rounded-l-lg',
      'in-data-input-wrapper:in-data-trailing:-ml-px in-data-input-wrapper:in-data-trailing:rounded-r-lg',
      // Size styles based on size when within an `InputGroup`. Every step of
      // the shared field-control scale is covered so the prefix box always
      // matches the height of the field beside it.
      // Font size tracks the responsive scale in select-shared.ts (xs and sm
      // step up at the `sm` breakpoint); md/lg/xl inherit the base `text-md`.
      // Without this the prefix text stays 16px while the field beside it drops
      // to 14px on mobile, and the two boxes render at different heights.
      'in-data-input-wrapper:in-data-[input-size=xs]:px-2.5 in-data-input-wrapper:in-data-[input-size=xs]:py-1.5 in-data-input-wrapper:in-data-[input-size=xs]:text-xs',
      'sm:in-data-input-wrapper:in-data-[input-size=xs]:text-sm',
      'in-data-input-wrapper:in-data-[input-size=sm]:px-3 in-data-input-wrapper:in-data-[input-size=sm]:py-2 in-data-input-wrapper:in-data-[input-size=sm]:text-sm',
      'sm:in-data-input-wrapper:in-data-[input-size=sm]:text-md',
      'in-data-input-wrapper:in-data-[input-size=md]:py-2.5 in-data-input-wrapper:in-data-[input-size=md]:pr-3 in-data-input-wrapper:in-data-[input-size=md]:pl-3.5',
      'in-data-input-wrapper:in-data-[input-size=lg]:px-4 in-data-input-wrapper:in-data-[input-size=lg]:py-3',
      'in-data-input-wrapper:in-data-[input-size=xl]:px-5 in-data-input-wrapper:in-data-[input-size=xl]:py-4',
      // Disabled styles
      isDisabled && 'border-disabled bg-disabled_subtle text-tertiary',
      'in-data-input-wrapper:group-disabled:bg-disabled_subtle in-data-input-wrapper:group-disabled:text-disabled in-data-input-wrapper:group-disabled:ring-border-disabled',

      props.className
    )}
  >
    {children}
  </span>
);

// `${string}ClassName` is used to omit any className prop that ends with a `ClassName` suffix
interface InputGroupProps extends Omit<
  InputBaseProps,
  | 'type'
  | 'icon'
  | 'placeholder'
  | 'tooltip'
  | 'shortcut'
  | 'size'
  | `${string}ClassName`
> {
  /** Input size — the full shared field-control scale, same as `Input`. */
  size?: SelectSize;
  /** A prefix text that is displayed in the same box as the input.*/
  prefix?: string;
  /** A leading addon that is displayed with visual separation from the input. */
  leadingAddon?: ReactNode;
  /** A trailing addon that is displayed with visual separation from the input. */
  trailingAddon?: ReactNode;
  /** The class name to apply to the input group. */
  className?: string;
  /** The children of the input group (i.e `<InputBase />`) */
  children: ReactNode;
  /** Controls whether error styling is shown. When false, suppresses error UI even if isInvalid is true. */
  showError?: boolean;
}

export const InputGroup = ({
  size = 'sm',
  prefix,
  leadingAddon,
  trailingAddon,
  label,
  hint,
  showError,
  children,
  ...props
}: InputGroupProps) => {
  const effectiveIsInvalid = (showError ?? true) && !!props.isInvalid;
  const hasLeading = !!leadingAddon;
  const hasTrailing = !!trailingAddon;

  // A floating trailing control (e.g. an inline icon-button) should sit inset
  // from the field's right edge to line up with InputBase's `right-1.5` wrapper,
  // so the same button reads identically whether the field is an Input or an
  // InputGroup. A full-height InputPrefix (%, min, https://) is a merged box that
  // must stay flush to the edge, so it opts out of the inset.
  const trailingIsPrefix =
    isValidElement(trailingAddon) && trailingAddon.type === InputPrefix;

  // `leadingText` carries no select-specific conditional, so its horizontal
  // padding is sourced from `sizes[step].root` in select-shared.ts rather than
  // a separate hard-coded literal. `sizes[step].root` also carries `py-*`/
  // `text-*` classes that don't belong on this span (it has no vertical
  // padding of its own — its height is 24px = the row's cross-axis center
  // driven by `my-auto`, not padding), so `py-0` cancels the vertical padding
  // back out; `text-*` is harmless since the `<p>` child sets its own
  // `text-md` regardless. `md` is the one exception: InputGroup's prefix has
  // never been mobile-responsive, so it keeps the static desktop-only
  // `pl-3.5` that mirrors the `sm:px-3.5` portion of `sizes.md.root`, rather
  // than picking up the mobile-first responsive behavior a direct reuse of
  // `sizes.md.root` would introduce.
  // `group-has-[&>select]` used to be unscoped: it fires whenever a <select>
  // exists ANYWHERE in the group, not specifically on the leading or trailing
  // side. That's indistinguishable for a composition with a select on ONE side
  // and a non-select addon on the OTHER (PhoneInput: a leading country <select>
  // + a trailing "add another number" button) — the trailing branch's
  // reservation fired off the leading select, stealing 12px of padding from
  // the wrong side of the number input and clipping its value. Scoping to the
  // `data-leading`/`data-trailing` section markers (already present on the
  // addon wrappers below) ties each reservation to the side it actually
  // describes. Descendant (`_`), not direct-child (`>`): a leadingAddon's
  // <select> is typically nested inside caller-supplied wrapper divs, not a
  // direct child of the marked section.
  const paddings = sortCx({
    xs: {
      input: cx(
        // Apply padding styles when select element is passed as a child
        hasLeading &&
          'group-has-[[data-leading]_select]:px-2 group-has-[[data-leading]_select]:pl-2',
        hasTrailing &&
          (prefix
            ? 'group-has-[[data-trailing]_select]:pr-5 group-has-[[data-trailing]_select]:pl-0'
            : 'group-has-[[data-trailing]_select]:pr-5 group-has-[[data-trailing]_select]:pl-2.5')
      ),
      leadingText: cx(sizes.xs.root, 'py-0'),
    },
    sm: {
      input: cx(
        // Apply padding styles when select element is passed as a child
        hasLeading &&
          'group-has-[[data-leading]_select]:px-2.5 group-has-[[data-leading]_select]:pl-2.5',
        hasTrailing &&
          (prefix
            ? 'group-has-[[data-trailing]_select]:pr-6 group-has-[[data-trailing]_select]:pl-0'
            : 'group-has-[[data-trailing]_select]:pr-6 group-has-[[data-trailing]_select]:pl-3')
      ),
      leadingText: cx(sizes.sm.root, 'py-0'),
    },
    md: {
      input: cx(
        // Apply padding styles when select element is passed as a child
        hasLeading &&
          'group-has-[[data-leading]_select]:px-3 group-has-[[data-leading]_select]:pl-3',
        hasTrailing &&
          (prefix
            ? 'group-has-[[data-trailing]_select]:pr-6 group-has-[[data-trailing]_select]:pl-0'
            : 'group-has-[[data-trailing]_select]:pr-6 group-has-[[data-trailing]_select]:pl-3')
      ),
      leadingText: 'pl-3.5',
    },
    lg: {
      input: cx(
        // Apply padding styles when select element is passed as a child
        hasLeading &&
          'group-has-[[data-leading]_select]:px-4 group-has-[[data-leading]_select]:pl-4',
        hasTrailing &&
          'group-has-[[data-trailing]_select]:pr-8 group-has-[[data-trailing]_select]:pl-0'
      ),
      leadingText: cx(sizes.lg.root, 'py-0'),
    },
    xl: {
      input: cx(
        // Apply padding styles when select element is passed as a child
        hasLeading &&
          'group-has-[[data-leading]_select]:px-5 group-has-[[data-leading]_select]:pl-5',
        hasTrailing &&
          'group-has-[[data-trailing]_select]:pr-10 group-has-[[data-trailing]_select]:pl-0'
      ),
      leadingText: cx(sizes.xl.root, 'py-0'),
    },
  });

  return (
    <TextField
      size={size}
      aria-label={typeof label === 'string' ? label : undefined}
      inputClassName={cx(paddings[size].input)}
      tooltipClassName={cx(
        hasTrailing && !hasLeading && 'group-has-[&>select]:right-0'
      )}
      wrapperClassName={cx(
        'z-10',
        // Apply styles based on the presence of leading or trailing elements
        hasLeading && 'rounded-l-none',
        hasTrailing && 'rounded-r-none',
        // When select element is passed as a child
        'group-has-[&>select]:bg-transparent group-has-[&>select]:shadow-none group-has-[&>select]:ring-0 group-has-[&>select]:focus-within:ring-0',
        // In `Input` component, there is "group-disabled" class so here we need to use "group-disabled:group-has-[&>select]" to avoid conflict
        'group-disabled:group-has-[&>select]:bg-transparent',
        // Library Select (button trigger, tagged data-select-trigger) gets
        // the same embedded treatment: the GROUP draws the single ring, so
        // the input's own chrome must drop.
        'group-has-[[data-select-trigger]]:bg-transparent group-has-[[data-select-trigger]]:shadow-none group-has-[[data-select-trigger]]:ring-0 group-has-[[data-select-trigger]]:focus-within:ring-0',
        'group-disabled:group-has-[[data-select-trigger]]:bg-transparent'
      )}
      {...props}
      isInvalid={effectiveIsInvalid}
    >
      {({
        isDisabled,
        isInvalid,
        isRequired,
      }: {
        isDisabled: boolean;
        isInvalid: boolean;
        isRequired: boolean;
      }) => (
        <>
          {label && <Label isRequired={isRequired}>{label}</Label>}

          <div
            data-input-size={size}
            className={cx(
              'group relative flex h-max w-full flex-row justify-center rounded-lg bg-primary transition-all duration-100 ease-linear',

              // Apply the field focus ring when a select-containing group has
              // ANY focused child — the amount input OR the trailing/leading
              // select. Using `has-[:focus]` (was `has-[input:focus]`) means
              // focusing the currency unit picker highlights the whole field
              // instead of the select drawing a detached ring around itself.
              'has-[select]:shadow-xs has-[select]:ring-1 has-[select]:ring-border-primary has-[select]:ring-inset has-[select]:has-[:focus]:ring-2 has-[select]:has-[:focus]:ring-border-brand',
              // Same treatment when the embedded picker is the LIBRARY
              // Select (button trigger tagged data-select-trigger) — the
              // native-element gate above can't see it, which left the
              // whole composite field borderless.
              'has-[[data-select-trigger]]:shadow-xs has-[[data-select-trigger]]:ring-1 has-[[data-select-trigger]]:ring-border-primary has-[[data-select-trigger]]:ring-inset has-[[data-select-trigger]]:has-[:focus]:ring-2 has-[[data-select-trigger]]:has-[:focus]:ring-border-brand',

              isDisabled &&
                'cursor-not-allowed has-[select]:bg-disabled_subtle has-[select]:ring-border-disabled has-[[data-select-trigger]]:bg-disabled_subtle has-[[data-select-trigger]]:ring-border-disabled',
              isInvalid &&
                'has-[select]:ring-border-error_subtle has-[select]:has-[:focus]:ring-border-error has-[[data-select-trigger]]:ring-border-error_subtle has-[[data-select-trigger]]:has-[:focus]:ring-border-error'
            )}
          >
            {leadingAddon && (
              // items-center vertically centers short addons (e.g. an inline
              // icon-button) within the field, matching InputBase's trailing-addon
              // wrapper. A full-height addon like InputPrefix naturally equals the
              // input height, so centering is a no-op for it.
              <section
                data-leading={hasLeading || undefined}
                className='flex items-center'
              >
                {leadingAddon}
              </section>
            )}

            {prefix && (
              <span
                // `leadingText` comes first so the explicit `pr-2` below always
                // wins over the right padding baked into `sizes[step].root`
                // (tailwind-merge keeps the last conflicting class).
                className={cx(paddings[size].leadingText, 'my-auto grow pr-2')}
              >
                <p
                  className={cx(
                    'text-md text-tertiary',
                    isDisabled && 'text-disabled'
                  )}
                >
                  {prefix}
                </p>
              </span>
            )}

            {children}

            {trailingAddon && (
              // items-center: center short addons; no-op for full-height
              // InputPrefix. Fixes the "+" add-another button riding at the top of
              // taller fields (e.g. phone, whose leading country select forces h-10).
              // pr-1.5: inset a floating control to match InputBase's right-1.5,
              // skipped for InputPrefix so its merged box stays flush to the edge.
              <section
                data-trailing={hasTrailing || undefined}
                className={cx(
                  'flex items-center',
                  // A floating control (inline icon-button) is inset by pr-1.5
                  // to line up with InputBase's right-1.5. A full-height merged
                  // end-cap — InputPrefix, or a <select> like the currency unit
                  // picker — must sit flush to the field's right edge; the inset
                  // otherwise leaves a visible gap between the select's rounded
                  // corner and the field border. has-[select]:pr-0 drops the
                  // inset only for select end-caps, keeping it for icon buttons.
                  !trailingIsPrefix &&
                    'pr-1.5 has-[select]:pr-0 has-[[data-select-trigger]]:pr-0'
                )}
              >
                {trailingAddon}
              </section>
            )}
          </div>

          {hint && (
            <HintText isInvalid={isInvalid} showError={showError}>
              {hint}
            </HintText>
          )}
        </>
      )}
    </TextField>
  );
};

InputGroup.Prefix = InputPrefix;

InputGroup.displayName = 'InputGroup';
